import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { IOrder } from '../../model/models/order/order-interface';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { BridgeAuthorize3DSEnrollmentRequest, BridgeAuthorize3DSEnrollmentResult, BridgeAuthorize3DSFiservRequest, BridgeAuthorize3DSFiservResult, BridgeMessageFromType } from '../../model/types/bridge-types';
import { ErrorCodeType } from '../../model/types/error-code-type';
import { SegmentProvider } from '../analytics/segment';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { PaymentProvider } from './payment';
import { VueProvider } from '../vue/vue';
import { TransitionType } from '../vue/vue-helpers';
import { RouteProvider } from '../../providers/route/route';
import { TranslateService } from '@ngx-translate/core';

/** How long the whole authorization (incl. user challenge/OTP) may take. */
const AUTHORIZE_RESULT_TIMEOUT_MS = 180000;
/** Fallback before revealing the iframe if the overlay-ready ack is lost. */
const OVERLAY_READY_FALLBACK_MS = 3000;

/** Bridge messages from the Vue side carry the uid of the request they answer. */
interface BridgeUidMessage {
  uid?: number;
  data?: any;
}

/**
 * Structural slice of OrderProvider used by the enrollment validate — declared
 * locally so this provider does not import the OrderProvider class (send-finish
 * passes its own instance, exactly like the native Cardinal flow does).
 */
interface Songbird3dsOrderProvider {
  validateOrder(order: IOrder, selfServicePspPaymentMethod?: any): Promise<any>;
}

/** Order context of an in-flight authorization, supplied by send-finish. */
export interface Songbird3dsAuthorizeContext {
  orderProvider: Songbird3dsOrderProvider;
  order: IOrder;
  sendFlowProvider: any;
}

interface PendingAuthorization {
  resolve: (value: BridgeAuthorize3DSFiservResult) => void;
  reject: (reason?: any) => void;
  timeout: any;
}

/**
 * 3DS Songbird bridge provider.
 *
 * Owns the CyberSource/Songbird 3DSV2 authorization flow that runs inside the
 * Vue app (secure site) and is orchestrated over the Vue bridge: request/ack/
 * result correlation by uid, timeouts, and iframe show/hide around the
 * challenge. The order itself never crosses the bridge — when Songbird's
 * device-data collection completes, the Vue side asks back for the enrollment
 * order/validate (Authorize3DSEnrollment) which runs HERE via OrderProvider,
 * keeping the native error handling and analytics.
 *
 * Only one authorization can be in flight at a time (same constraint as the
 * native Cardinal flow, where the SDK holds a single global session).
 *
 * VueProvider never imports this class — it is late-bound via
 * registerSongbird3dsBridge() in the constructor to avoid a DI cycle.
 */
@Injectable()
export class Songbird3dsBridgeProvider {
  private requestUid = 0;
  private pendingRequests = new Map<number, PendingAuthorization>();
  private pendingReady = new Map<number, () => void>();
  private activeContext: Songbird3dsAuthorizeContext | null = null;

  constructor(
    private vueProvider: VueProvider,
    private paymentProvider: PaymentProvider,
    private analytics: SegmentProvider,
    private routeProvider: RouteProvider,
    private translate: TranslateService
  ) {
    this.vueProvider.registerSongbird3dsBridge(this);
  }

  /**
   * True when the bridge transport is (or could be made) available for the
   * 3DS flow. The policy lives here: the 3DS flow is allowed to use the bridge
   * transport even when the general rmt5 flag keeps the UI bridge disabled, so
   * enable our scope first, then run the generic self-healing connect.
   */
  ensureAvailable(): Promise<boolean> {
    this.vueProvider.enableSongbird3dsScope();
    return this.vueProvider.ensureBridgeConnected();
  }

  /**
   * Run the CyberSource/Songbird 3DSV2 authorization in the Vue app.
   *
   * @param request  Tokens/ids for Cardinal (the Songbird script config is
   *                 attached here from APP_CONFIG; the order is NOT sent).
   * @param context  Order context used to serve the enrollment validate the
   *                 Vue side requests back mid-flow.
   * @returns        The payer-authentication result (JWT/transaction ids), or
   *                 `cancelled` when the user aborted the challenge.
   */
  async authorize(
    request: BridgeAuthorize3DSFiservRequest,
    context: Songbird3dsAuthorizeContext,
    timeoutMs = AUTHORIZE_RESULT_TIMEOUT_MS
  ): Promise<BridgeAuthorize3DSFiservResult> {
    if (this.activeContext) {
      // A second concurrent flow would clobber the enrollment context of the
      // first — refuse it instead of failing both in confusing ways.
      throw new Error('A 3DSV2 authorization is already in progress');
    }

    const connected = await this.ensureAvailable();
    if (!connected) {
      throw new Error('Vue bridge is not connected - cannot run 3DSV2 authorization');
    }

    this.activeContext = context;
    const uid = ++this.requestUid;
    try {
      const resultPromise = this.createResultPromise(uid, timeoutMs);


      this.vueProvider.elevateIframe = true;
      const overlayReady = this.createOverlayReadyPromise(uid);
      this.vueProvider.postMessage(BridgeMessageFromType.Authorize3DSFiserv, { uid, data: this.withScriptConfig(request) });
      await overlayReady;
      await this.vueProvider.showVueBridgeIframe();

      return await resultPromise;
    } finally {
      this.activeContext = null;
      this.clearPending(uid);
      await this.vueProvider.hideVueBridgeIframe(TransitionType.DESTRUCT);
    }
  }

  private clearPending(uid: number) {
    const pending = this.pendingRequests.get(uid);
    if (pending) {
      window.clearTimeout(pending.timeout);
      this.pendingRequests.delete(uid);
    }
    this.pendingReady.delete(uid);
  }

  /**
   * Called by VueProvider when the Vue side asks for the 3DS enrollment
   * order/validate (after Songbird device-data collection). Runs the validate
   * natively with the same error handling/analytics as the native Cardinal
   * flow, and replies with the outcome the Vue side needs to branch on.
   */
  async handleEnrollmentRequest(message: BridgeUidMessage) {
    const request: BridgeAuthorize3DSEnrollmentRequest = (message && message.data) || {};
    const reply = (data: BridgeAuthorize3DSEnrollmentResult) => {
      this.vueProvider.postMessage(BridgeMessageFromType.Authorize3DSEnrollmentResult, { data }, message);
    };

    const context = this.activeContext;
    if (!context) {
      LoggerProvider.error(this, LoggerEvent.Payment, 'Received 3DS enrollment request with no active authorization');
      reply({ ok: false, error: 'No active 3DS authorization' });
      return;
    }
    if (!request.referenceId) {
      LoggerProvider.error(this, LoggerEvent.Payment, 'Received 3DS enrollment request without a reference ID');
      reply({ ok: false, error: 'Missing payer auth reference ID' });
      return;
    }

    const validation = await this.runEnrollmentValidate(context, request);
    if (!validation) {
      reply({ ok: false, error: 'Order validation failed' });
      return;
    }

    reply({
      ok: true,
      transactionId: validation.transactionId,
      payerAuthenticateResponse: validation.payerAuthenticateResponse || null,
      errors: validation.errors || [],
    });
  }

  /** Called by VueProvider when the Vue app replies with the authorization result. */
  handleResult(message: BridgeUidMessage) {
    const uid = message && message.uid;
    const pending = this.pendingRequests.get(uid);
    if (!pending) {
      LoggerProvider.error(this, LoggerEvent.Vue, 'Received authorize-3ds-fiserv result for unknown uid', { uid });
      return;
    }
    window.clearTimeout(pending.timeout);
    this.pendingRequests.delete(uid);

    const data: BridgeAuthorize3DSFiservResult = (message && message.data) || { success: false };
    if (data.success) {
      pending.resolve(data);
    } else {
      pending.reject(new Error(data.error || 'authorize-3ds-fiserv failed'));
    }
  }

  /** Called by VueProvider when the Vue app acks that its overlay is mounted. */
  handleReady(message: BridgeUidMessage) {
    const uid = message && message.uid;
    const readyResolve = this.pendingReady.get(uid);
    if (readyResolve) {
      readyResolve();
    }
  }

  private createResultPromise(uid: number, timeoutMs: number): Promise<BridgeAuthorize3DSFiservResult> {
    return new Promise<BridgeAuthorize3DSFiservResult>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pendingRequests.delete(uid);
        reject(new Error(`authorize-3ds-fiserv timed out after ${timeoutMs} ms`));
      }, timeoutMs);
      this.pendingRequests.set(uid, { resolve, reject, timeout });
    });
  }

  private createOverlayReadyPromise(uid: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        this.pendingReady.delete(uid);
        resolve();
      }, OVERLAY_READY_FALLBACK_MS);
      this.pendingReady.set(uid, () => {
        window.clearTimeout(timer);
        this.pendingReady.delete(uid);
        resolve();
      });
    });
  }

  /**
   * Hand oneApp's Songbird config to the Vue side so both paths load the exact
   * same SDK build, with the same SRI hash. The hash key only exists in envs
   * that pin a versioned build (edge builds are mutable and can't be pinned),
   * so read it defensively.
   */
  private withScriptConfig(request: BridgeAuthorize3DSFiservRequest): BridgeAuthorize3DSFiservRequest {
    const withConfig: BridgeAuthorize3DSFiservRequest = {
      cardinalScriptUrl: APP_CONFIG.cardinalScript,
      cardinalScriptIntegrityHash: (APP_CONFIG as any).cardinalScriptIntegrityHash,
      ...request,
    };
    return withConfig;
  }

  /**
   * Native enrollment validate with the same error handling as the native
   * Cardinal flow (payment.ts authorize3DSV2Card): on failure the validation
   * payload inside the error still drives the flow, the verification-failed
   * event fires, and the known error codes get their analytics.
   */
  private async runEnrollmentValidate(
    context: Songbird3dsAuthorizeContext,
    request: BridgeAuthorize3DSEnrollmentRequest
  ): Promise<any> {
    const { orderProvider, order, sendFlowProvider } = context;
    const payerAuthOrder: IOrder = { ...order };
    payerAuthOrder.payerAuthSetupReferenceId = request.referenceId;
    payerAuthOrder.transactionId = Number(request.transactionId);

    try {
      return await orderProvider.validateOrder(payerAuthOrder);
    } catch (ex) {
      const validation = ex && ex.error;
      try {
        if (order && orderProvider && ex)
          await this.paymentProvider.generateOrderVerificationFailedEvent(order, ex, orderProvider, sendFlowProvider);
      } catch (eventEx) {
        LoggerProvider.exception(this, eventEx, LoggerEvent.Payment, 'Error when firing TransactionVerificationFailed');
        return false;
      }

      const firstErrorCode = validation && validation.errors && validation.errors.length > 0
        ? validation.errors[0].errorCode
        : null;
      if (firstErrorCode == ErrorCodeType.CardNotEnrolledInPayerAuthentication) {
        LoggerProvider.error(this, LoggerEvent.Payment, 'Card not enrolled in payer authentication', validation);
        this.routeProvider.openModal(this.routeProvider.pages.SendFailedPage, {missing: ex.errorCode, error: validation, digitalError: true, goBackToSendSummary: true});
        return false;
      } else if (firstErrorCode === ErrorCodeType.InvalidPaymentMethod) {
        // `location` mirrors the native flow's analytics payload verbatim
        this.analytics.trackEvent({
          eventName: AnalyticsEventType.TransferResendFailed, analyticsData: {
            location: location,
            errorType: 'InvalidPaymentMethod'
          }
        });
        this.invalidPaymentMethodErrorScreen();
        return false;
      }
      return validation;
    }
  }

  invalidPaymentMethodErrorScreen() {
    let params = {
      errorTitle: this.translate.instant('page.errorMessage.ria_errormessage_paymentinvalidheader'),
      errorDetails: this.translate.instant('page.errorMessage.ria_errormessage_paymentinvalidbody'),
      buttonText: this.translate.instant('global.myRia.jsonContent.CancelTransactionButton'),
      fullButtonClick: () => this.routeProvider.replace(this.routeProvider.pages.CheckPricesPage),
      clickClose: () => this.routeProvider.pop(),
      showBullets: true,
      newDesign: true,
      showFullButton: true,
      isShowHelp: false,
      isShowClose: true
    }

    this.routeProvider.openModal(this.routeProvider.pages.SendFailedPage, params);
  }
}



// WEBPACK FOOTER //
// ./src/providers/payment/songbird-3ds-bridge.ts